/**
 * Frontend cache service with expiration support
 */

// Cache expiration time in seconds (5 minutes - matches backend cache)
const CACHE_EXPIRATION = 300;

// In-memory cache store
const cache = new Map();

/**
 * Generate a cache key from filter object
 * @param {Object} filter - Filter object
 * @returns {string} Cache key
 */
function generateCacheKey(endpoint, filter) {
    // Create a stable string representation of the filter
    const filterStr = JSON.stringify(filter || {});
    return `${endpoint}:${filterStr}`;
}

/**
 * Check if cached data is still valid
 * @param {Object} cacheEntry - Cache entry with data and timestamp
 * @returns {boolean} True if cache is still valid
 */
function isCacheValid(cacheEntry) {
    if (!cacheEntry || !cacheEntry.timestamp) {
        return false;
    }
    const now = Date.now();
    const age = (now - cacheEntry.timestamp) / 1000; // Age in seconds
    return age < CACHE_EXPIRATION;
}

/**
 * Store data in cache
 * @param {string} endpoint - API endpoint
 * @param {Object} filter - Filter object
 * @param {*} data - Data to cache
 */
function setCachedData(endpoint, filter, data) {
    const key = generateCacheKey(endpoint, filter);
    const timestamp = Date.now();
    cache.set(key, {
        data: data,
        timestamp: timestamp
    });
}

/**
 * Clear cache for a specific endpoint and filter, or clear all cache
 * @param {string} endpoint - API endpoint (optional)
 * @param {Object} filter - Filter object (optional)
 */
function clearCache(endpoint, filter) {
    if (endpoint && filter) {
        const key = generateCacheKey(endpoint, filter);
        cache.delete(key);
    } else {
        cache.clear();
    }
}

/**
 * Get cached data if valid, otherwise fetch fresh data
 * @param {string} endpoint - API endpoint
 * @param {Object} filter - Filter object
 * @param {Function} fetchFn - Function to fetch fresh data
 * @returns {Promise<*>} Fresh or cached data
 */
async function getCachedOrFetch(endpoint, filter, fetchFn) {
    const key = generateCacheKey(endpoint, filter);
    const cacheEntry = cache.get(key);
    
    // If cache exists and is valid, return it without API call
    if (cacheEntry && isCacheValid(cacheEntry)) {
        return cacheEntry.data;
    }
    
    // Cache doesn't exist or is expired, fetch fresh data
    const freshData = await fetchFn();
    if (freshData) {
        setCachedData(endpoint, filter, freshData);
    }
    return freshData;
}

export default {
    clearCache,
    getCachedOrFetch
};

